
import React from 'react';
import { Product } from '../types';
import { Edit, Trash2, ArrowUpRight, ShieldCheck, Zap, ExternalLink } from 'lucide-react';
import { getBasePath } from '../constants';

interface ProductCardProps {
  product: Product;
  variant: 'vertical' | 'horizontal' | 'bento';
  isAdmin: boolean;
  onEdit: (product: Product) => void;
  onDelete: (id: string) => void;
  onTrack: (type: 'product' | 'news', id: string) => void;
  onClick?: (product: Product) => void;
}

const ProductCard: React.FC<ProductCardProps> = ({ product, variant, isAdmin, onEdit, onDelete, onTrack, onClick }) => {
  
  const handleAction = (e: React.MouseEvent) => {
    onTrack('product', product.id);
    if (onClick) {
      e.preventDefault();
      onClick(product);
    }
  };

  const productUrl = `${getBasePath()}/produtos/${product.slug || product.id}`;

  const isBentoOrVertical = variant === 'bento' || variant === 'vertical';

  if (isBentoOrVertical) {
    return (
      <div className="group relative h-full w-full rounded-2xl overflow-hidden bg-white border border-gray-100 shadow-sm transition-all duration-700 hover:shadow-xl hover:-translate-y-1">
        {/* Badge Categoria */}
        <div className="absolute top-3 left-3 md:top-4 md:left-4 z-20 pointer-events-none">
            <span className="px-3 py-1 bg-white/90 backdrop-blur-md rounded-lg text-[9px] font-black uppercase tracking-widest text-primary shadow-lg border border-gray-100 flex items-center gap-2">
                <Zap size={10} fill="currentColor" /> {product.category === 'featured' ? 'Premium' : 'Expert'}
            </span>
        </div>

        {/* Link Envolvendo a Imagem */}
        <button 
          onClick={handleAction}
          className="relative block w-full aspect-video overflow-hidden bg-gray-100"
        >
          <img 
            src={product.imageUrl} 
            alt={product.title} 
            className="w-full h-full object-cover transition-transform duration-[2000ms] ease-out group-hover:scale-110"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/5 to-transparent opacity-40"></div>
        </button>

        {/* Conteúdo Editorial */}
        <div className="p-5 md:p-6 flex flex-col h-full">
          <button 
            onClick={handleAction}
            className="block text-left no-underline group/title"
          >
            <h3 className="text-lg md:text-xl font-black text-gray-900 leading-tight uppercase tracking-tighter mb-2 group-hover/title:text-primary transition-colors line-clamp-1">
              {product.title}
            </h3>
          </button>
          
          <p className="text-[11px] md:text-xs text-gray-500 font-medium leading-relaxed mb-6 line-clamp-2">
            {product.description ? product.description.replace(/<[^>]*>/g, '') : ''}
          </p>
          
          <div className="mt-auto flex items-center justify-between border-t border-gray-50 pt-4">
            <a 
              href={product.link}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => onTrack('product', product.id)}
              className="inline-flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-900 hover:text-primary transition-all no-underline bg-gray-50 hover:bg-primary/5 px-4 py-2 rounded-lg"
            >
              Acessar <ArrowUpRight size={14} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            
            <div className="flex items-center gap-1.5 text-[9px] font-bold text-emerald-500 uppercase tracking-widest opacity-60">
              <ShieldCheck size={12} /> Disponível
            </div>
          </div>
        </div>

        {/* Admin Tools */}
        {isAdmin && (
          <div className="absolute top-3 right-3 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-30">
              <button 
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); onEdit(product); }}
                className="p-2 bg-white/90 backdrop-blur-md text-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-all shadow-xl border border-gray-100"
              >
                  <Edit size={14} />
              </button>
              <button 
                 onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(product.id); }}
                 className="p-2 bg-white/90 backdrop-blur-md text-red-600 rounded-lg hover:bg-red-600 hover:text-white transition-all shadow-xl border border-gray-100"
              >
                  <Trash2 size={14} />
              </button>
          </div>
        )}
      </div>
    );
  }

  // Variante Horizontal (Compacta)
  return (
    <div className="group relative bg-white rounded-xl overflow-hidden border border-gray-100 flex p-2.5 gap-3 shadow-sm hover:shadow-lg transition-all duration-500">
       <button 
        onClick={handleAction}
        className="w-16 h-16 shrink-0 relative rounded-lg overflow-hidden shadow-inner block"
       >
          <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
       </button>
       <div className="flex-1 min-w-0 flex flex-col justify-center">
          <button onClick={handleAction} className="text-left no-underline">
            <h3 className="text-xs font-black text-gray-900 leading-none mb-1 truncate uppercase tracking-tighter hover:text-primary transition-colors">{product.title}</h3>
          </button>
          <p className="text-[9px] text-gray-500 line-clamp-1 mb-1 font-medium">
            {product.description ? product.description.replace(/<[^>]*>/g, '') : ''}
          </p>
          <div className="flex items-center gap-3">
            <a href={product.link} onClick={() => onTrack('product', product.id)} target="_blank" rel="noreferrer" className="text-[8px] font-black text-primary uppercase tracking-[0.1em] flex items-center gap-1 no-underline hover:gap-2 transition-all">
              Acessar <ExternalLink size={10} />
            </a>
            <button onClick={handleAction} className="text-[8px] font-black text-gray-400 uppercase tracking-[0.1em] flex items-center gap-1 no-underline hover:text-primary transition-all">
              Detalhes <ArrowUpRight size={10} />
            </button>
          </div>
       </div>
    </div>
  );
};

export default ProductCard;
